import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface UrbanInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const UrbanInsights: React.FC<UrbanInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Spring entrance animation on bullet text
  const textOpacity = interpolate(elapsedFrames, [0, 5], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.88,
    to: 1,
    config: { damping: 14, stiffness: 160, mass: 0.45 },
  });

  const textTranslateY = spring({
    frame: elapsedFrames,
    fps,
    from: 24,
    to: 0,
    config: { damping: 14, stiffness: 160, mass: 0.45 },
  });

  // Bottom accent line: width interpolates 0→100% over first 20 frames of bullet
  const accentLineWidth = interpolate(elapsedFrames, [0, 20], [0, 100], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Pad index numbers
  const padIndex = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100%",
        background: "#0a0a0a",
        borderLeft: "6px solid #ff2222",
        padding: "36px",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Huge background number watermark */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          left: "20px",
          fontFamily: "'Bebas Neue', cursive",
          fontSize: "96px",
          color: "rgba(255,204,0,0.15)",
          lineHeight: 1,
          userSelect: "none",
          pointerEvents: "none",
          letterSpacing: "-2px",
        }}
      >
        {padIndex(activeIndex + 1)}
      </div>

      {/* Dot pagination + brand badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          {bullets.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === activeIndex ? 20 : 6,
                height: 6,
                borderRadius: 0,
                background: i === activeIndex ? "#ffcc00" : "rgba(255,34,34,0.3)",
              }}
            />
          ))}
        </div>
        {(() => {
          const brand = detectBrandInText(activeBullet.text);
          return brand ? (
            <CompanyBadge
              name={brand}
              color="#ffcc00"
              accentColor="#ff2222"
              size={72}
              style="badge"
              opacity={textOpacity}
            />
          ) : null;
        })()}
      </div>

      {/* Bullet text with spring entrance */}
      <div
        style={{
          opacity: textOpacity,
          transform: `scale(${textScale}) translateY(${textTranslateY}px)`,
          transformOrigin: "left center",
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <p
          style={{
            fontFamily: "'Bebas Neue', cursive",
            fontSize: "68px",
            color: "#f5f5f5",
            letterSpacing: "2px",
            lineHeight: 1.15,
            textTransform: "uppercase",
            margin: 0,
            padding: 0,
          }}
        >
          <HighlightText
            highlightStyle={{
              fontWeight: 900,
              color: "#ff2222",
              textTransform: "uppercase",
              background: "#ffcc00",
              padding: "0 8px",
              display: "inline-block",
              transform: "skew(-10deg)",
            }}
            text={activeBullet.text}
            highlights={activeBullet.highlights}
          />
        </p>
      </div>

      {/* Bottom accent line — animates width from 0 to 100% */}
      <div
        style={{
          position: "absolute",
          bottom: "36px",
          left: "36px",
          right: "36px",
          height: "2px",
          background: "#333333",
          zIndex: 2,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${accentLineWidth}%`,
            background: "#ffcc00",
          }}
        />
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
